import { queryOptions } from '@tanstack/react-query'
import type { TadaDocumentNode, VariablesOf } from 'gql.tada'

import { graphqlClient } from '@/lib/graphql-request-client'
import {
  GET_ACCOUNTS,
  GET_ACCOUNT_OFFERS,
  GET_CALL_PHONES,
  GET_HEART_ABILITIES,
  GET_ME,
  GET_OFFER_EMAIL_CONFLICTS,
  GET_PHONE_NUMBER_REQUESTS,
  GET_PRICING_TEMPLATES,
  GET_VIXIEES_USERS,
} from '@/graphql/queries'

// --- Query Key Constants ---
// Exported for use with invalidateQueries (HOOK-04).
// TanStack Query matches keys by prefix, so invalidating queryKeys.accounts
// will invalidate all account query variants regardless of params.
export const queryKeys = {
  me: ['me'] as const,
  accounts: ['accounts'] as const,
  accountOffers: ['accountOffers'] as const,
  offerEmailConflicts: ['offerEmailConflicts'] as const,
  pricingTemplates: ['pricingTemplates'] as const,
  vixieesUsers: ['vixieesUsers'] as const,
  heartAbilities: ['heartAbilities'] as const,
  callPhones: ['callPhones'] as const,
  phoneNumberRequests: ['phoneNumberRequests'] as const,
}

// --- Query Fn Helper ---
// Wires the TanStack Query AbortSignal through to the GraphQL client so
// in-flight requests are cancelled when the last observer unmounts.

const gqlQuery =
  <TResult, TVariables>(
    document: TadaDocumentNode<TResult, TVariables>,
    variables?: TVariables,
  ) =>
  ({ signal }: { signal: AbortSignal }): Promise<TResult> =>
    graphqlClient.request(document, variables, { signal })

// --- Query Options Factories ---

export const meQueryOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.me],
    queryFn: gqlQuery(GET_ME),
    staleTime: 60 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000,
  })

export const accountsQueryOptions = (
  params: VariablesOf<typeof GET_ACCOUNTS>,
) =>
  queryOptions({
    queryKey: [...queryKeys.accounts, params],
    queryFn: gqlQuery(GET_ACCOUNTS, params),
    staleTime: 30_000,
    gcTime: 5_000,
    refetchOnWindowFocus: true,
  })

export const accountOffersQueryOptions = (
  params: VariablesOf<typeof GET_ACCOUNT_OFFERS>,
) =>
  queryOptions({
    queryKey: [...queryKeys.accountOffers, params],
    queryFn: gqlQuery(GET_ACCOUNT_OFFERS, params),
    staleTime: 30_000,
    gcTime: 5_000,
    refetchOnWindowFocus: true,
  })

export const offerEmailConflictsQueryOptions = (
  params: VariablesOf<typeof GET_OFFER_EMAIL_CONFLICTS>,
) =>
  queryOptions({
    queryKey: [...queryKeys.offerEmailConflicts, params],
    queryFn: gqlQuery(GET_OFFER_EMAIL_CONFLICTS, params),
    staleTime: 30_000,
  })

export const pricingTemplatesQueryOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.pricingTemplates],
    queryFn: gqlQuery(GET_PRICING_TEMPLATES),
    staleTime: 60 * 60 * 1000,
  })

export const vixieesUsersQueryOptions = (
  params: VariablesOf<typeof GET_VIXIEES_USERS>,
) =>
  queryOptions({
    queryKey: [...queryKeys.vixieesUsers, params],
    queryFn: gqlQuery(GET_VIXIEES_USERS, params),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

export const heartAbilitiesQueryOptions = () =>
  queryOptions({
    queryKey: [...queryKeys.heartAbilities],
    queryFn: gqlQuery(GET_HEART_ABILITIES),
    staleTime: 5 * 60_000,
  })

export const callPhonesQueryOptions = (
  params: VariablesOf<typeof GET_CALL_PHONES>,
) =>
  queryOptions({
    queryKey: [...queryKeys.callPhones, params],
    queryFn: gqlQuery(GET_CALL_PHONES, params),
    staleTime: 30_000,
    gcTime: 5_000,
    refetchOnWindowFocus: true,
  })

export const phoneNumberRequestsQueryOptions = (
  params: VariablesOf<typeof GET_PHONE_NUMBER_REQUESTS>,
) =>
  queryOptions({
    queryKey: [...queryKeys.phoneNumberRequests, params],
    queryFn: gqlQuery(GET_PHONE_NUMBER_REQUESTS, params),
    staleTime: 30_000,
    gcTime: 5_000,
    refetchOnWindowFocus: true,
  })
